import { defineStore } from "pinia";
import { getSecureCookieOptions } from "@/utils/cookieOptions";
import { useCheckoutStore } from "@/store/checkoutStore";

export const useShoppingCartStore = defineStore("shoppingCartStore", {
  state: () => ({
    shoppingCartId: null,
    products: [],
    initialized: false,
  }),
  getters: {
    totalProducts(state) {
      return state.products.reduce((sum, product) => sum + product.quantity, 0);
    },
    totalProductPrice(state) {
      return state.products.reduce(
        (sum, product) => sum + product.quantity * product.price,
        0
      );
    },
    totalPrice() {
      const checkoutStore = useCheckoutStore();
      const shippingCost = checkoutStore.shippingCost?.price || 0;
      return this.totalProductPrice + shippingCost;
    },
    shippingCost() {
      const checkoutStore = useCheckoutStore();
      return checkoutStore.shippingCost?.price || 0;
    },
  },
  actions: {
    // Initialize store values, possibly from an API or persisted cookies
    initialize({ shoppingCartId, products }) {
      this.shoppingCartId = shoppingCartId;
      this.products =
        products.map((product) => ({
          sku: product.sku,
          quantity: product.quantity,
          price: product.price,
          name: product.name,
          currency: product.currency,
        })) || [];

      // Also update cookies if needed:
      this.setShoppingCartId(shoppingCartId);
    },
    createCart({ shoppingCartId }) {
      this.products = [];

      this.setShoppingCartId(shoppingCartId);
    },
    updateCart(sku, quantity, name) {
      // Create product if it does not exist
      // if quantity is null or undefined remove the product
      // remove product if quantity is 0 or lower after calculation of total quantity
      if (quantity === null || quantity === undefined) {
        this.products = this.products.filter((product) => product.sku !== sku);
      } else {
        const itemIndex = this.products.findIndex(
          (product) => product.sku === sku
        );
        if (itemIndex !== -1) {
          const newQuantity = this.products[itemIndex].quantity + quantity;
          if (newQuantity <= 0) {
            this.products.splice(itemIndex, 1);
          } else {
            this.products[itemIndex].quantity = newQuantity;
          }
        } else {
          this.products.push({ sku, quantity: quantity, name });
        }
      }
    },
    deleteCartItem(sku) {
      this.products = this.products.filter((product) => product.sku !== sku);
    },
    clear() {
      this.products = [];
      this.setShoppingCartId(null);
    },
    setShoppingCartId(shoppingCartId) {
      this.shoppingCartId = shoppingCartId;
    },
  },
  persist: [
    {
      storage: piniaPluginPersistedstate.cookies(getSecureCookieOptions()),
      pick: ["shoppingCartId"],
    },
    {
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ["products"],
    },
  ],
});
